import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import {useGetProductionPreview} from '../../../hooks/productions/useProductions';
import {GetProductionPreviewRequestParam} from '../../../api/types/production.type';
import '../Demo.scss';

const ProductionPreviewApiTest: React.FC = () => {
  const [productionId, setProductionId] = useState<number>(308);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: GetProductionPreviewRequestParam = {
    productionId: productionId,
  };

  const {
    data: previewData,
    isLoading,
    error,
  } = useGetProductionPreview(requestParams, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (previewData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(previewData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Production Preview API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get production preview file information including file existence and
          file path.
        </Typography>
      </Box>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" data-testid={`production-preview-chip-api-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Production Preview
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/preview/{'{productionId}'}
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get production preview file information including whether the file
            exists and the file path.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Path Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
              fullWidth
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2, marginTop: 2}}>
            <Button
              variant="contained"
              onClick={handleTestApi}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Test API'}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              Reset
            </Button>
            {previewData && (
              <Button variant="outlined" onClick={handleCopyJson}>
                {copySuccess ? 'Copied!' : 'Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Response */}
      {error && (
        <Alert severity="error" sx={{marginBottom: 3}}>
          <Typography className="typo-text body2_size">
            <strong>Error:</strong> {error.message}
          </Typography>
        </Alert>
      )}

      {previewData && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Response
            </Typography>
            <Paper
              sx={{
                padding: 2,
                backgroundColor: '#f5f5f5',
                overflow: 'auto',
                maxHeight: '400px',
              }}
            >
              <pre style={{margin: 0, fontSize: '12px'}}>
                {JSON.stringify(previewData, null, 2)}
              </pre>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Response Details */}
      {previewData && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Response Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    className="typo-text body3_size"
                    sx={{fontWeight: 'bold', marginBottom: 1}}
                  >
                    File Exists:
                  </Typography>
                  <Chip
                    label={previewData.fileExists ? 'Yes' : 'No'}
                    color={previewData.fileExists ? 'success' : 'error'}
                    size="small"
                    data-testid={`production-preview-chip-file-exists`}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    className="typo-text body3_size"
                    sx={{fontWeight: 'bold', marginBottom: 1}}
                  >
                    File Name:
                  </Typography>
                  <Typography
                    className="typo-text body3_size"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f5f5f5',
                      padding: '8px',
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                    }}
                  >
                    {previewData.fileName}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ProductionPreviewApiTest;
